/**
 * Erkennung von Barcode-Scannern im Tastaturmodus (USB/Bluetooth „Keyboard
 * Wedge"): Der Scanner tippt den Code als Zeichenfolge (Ziffern, bei Gutscheinen auch
 * Buchstaben und Bindestriche) in Millisekunden-
 * Abständen und schließt mit Enter (oder Tab) ab. Ziffern werden deshalb kurz
 * zurückgehalten; folgt binnen `maxAbstandMs` die nächste Ziffer und am Ende
 * Enter mit mindestens `minLaenge` Zeichen, ist es ein Scan. Sonst werden die
 * zurückgehaltenen Tasten unverändert freigegeben – ein Mensch tippt langsamer
 * und merkt die Verzögerung nicht.
 *
 * PB-1 (kurze Codes): Die Länge allein entscheidet nicht mehr.
 * - Zeitregel: ab `minLaenge` (Standard 4) Zeichen in schneller Folge mit Abschlusszeichen.
 * - Scannerpräfix (je Gerät eingerichtet, z. B. `F9`): danach ist jede Länge (1–48 druckbare
 *   Zeichen) bis zum Abschlusszeichen ein Scan; mit Präfix gilt die Zeitregel erst ab
 *   `minLaengeMitPraefix` (Standard 8) – weniger Fehlerkennung schneller Tipper.
 * - Eine schnelle Folge, die für die Zeitregel zu kurz ist (z. B. „7“ + Enter vom Scanner ohne
 *   Präfix), wird **verworfen** (`onVerworfen`): Sie erreicht nie den Ziffernblock (keine
 *   Menge, kein Betrag, kein „Hinzufügen“ per Enter). Solche Codes gehen nur über die Suche.
 */
import { DoppelScanFilter } from './barcode';

export interface GehalteneTaste {
  key: string;
  /** Element, das die Taste ursprünglich erhalten hätte (für die Freigabe). */
  ziel: unknown;
}

export interface Zeitgeber {
  setze(rueckruf: () => void, ms: number): unknown;
  loesche(handle: unknown): void;
}

export interface ScanErkennungOptionen {
  onScan: (code: string) => void;
  /** Zurückgehaltene Tasten doch an die Oberfläche weiterreichen (in Reihenfolge). */
  onFreigabe: (tasten: readonly GehalteneTaste[]) => void;
  /**
   * Schnelle Folge verworfen (PB-1): `kurz` = für die Zeitregel zu kurz (ohne Präfix),
   * `unvollstaendig` = nach dem Präfix kam kein Abschlusszeichen. Die Tasten sind verworfen.
   */
  onVerworfen?: (code: string, grund: 'kurz' | 'unvollstaendig') => void;
  /** Scannerpräfix (Wert von `KeyboardEvent.key`) – bei jeder Taste gelesen; null = keins. */
  praefix?: () => string | null;
  /** Größter Abstand zweier Scannerzeichen (Standard 50 ms). */
  maxAbstandMs?: number;
  /** Kürzester Code für die Zeitregel ohne Präfix (Standard 4). */
  minLaenge?: number;
  /** Kürzester Code für die Zeitregel, wenn ein Präfix eingerichtet ist (Standard 8). */
  minLaengeMitPraefix?: number;
  /** Längster Code nach der Zeitregel (Standard 20: GTIN-14 bzw. Gutscheincode mit 17 Zeichen). */
  maxLaenge?: number;
  /** Größter Abstand der Zeichen nach dem Präfix, sonst unvollständig (Standard 300 ms). */
  praefixAbstandMs?: number;
  zeitgeber?: Zeitgeber;
  /**
   * Go-Live Lebensmittelhandel: derselbe Code binnen `DOPPELSCAN_MS` noch einmal gemeldet
   * (doppeltes Ereignis desselben Scans) wird verworfen und protokolliert – ein absichtlicher
   * zweiter Scan danach zählt. null schaltet den Filter ab.
   */
  doppelFilter?: DoppelScanFilter | null;
}

/** Längster Code nach einem Präfix (wie die Codes im Stamm, Migration 0050). */
export const MAX_PRAEFIX_CODE = 48;

/**
 * Taugt eine Taste als Scannerpräfix? Nur Funktionstasten, die weder die Kasse (F2–F6) noch
 * der Browser bzw. das System belegen (F1 Hilfe, F7 Caret-Browsing, F10 Menü, F11, F12):
 * F8, F9 und F13–F24. Zeichen sind ausgeschlossen – sie kommen in Beträgen, E-Mail-Adressen
 * und Anschriften vor und würden dort verschluckt (Prüfung R1, M1).
 */
export function istGueltigesPraefix(key: string): boolean {
  return /^F(8|9|1[3-9]|2[0-4])$/.test(key);
}

const PRAEFIX_SCHLUESSEL = 'bilanx.scanner.praefix';

interface PraefixSpeicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
  removeItem(schluessel: string): void;
}

/** Scannerpräfix dieses Geräts (Einrichtung); null = keins bzw. ungültig/unlesbar. */
export function ladeScannerPraefix(speicher: PraefixSpeicher): string | null {
  try {
    const p = speicher.getItem(PRAEFIX_SCHLUESSEL);
    return p !== null && istGueltigesPraefix(p) ? p : null;
  } catch {
    return null;
  }
}

/** Scannerpräfix setzen (null entfernt); ungültige Tasten werden abgelehnt (false). */
export function setzeScannerPraefix(speicher: PraefixSpeicher, praefix: string | null): boolean {
  try {
    if (praefix === null) {
      speicher.removeItem(PRAEFIX_SCHLUESSEL);
      return true;
    }
    if (!istGueltigesPraefix(praefix)) {
      return false;
    }
    speicher.setItem(PRAEFIX_SCHLUESSEL, praefix);
    return ladeScannerPraefix(speicher) === praefix;
  } catch {
    return false;
  }
}

/** Meldung zu einem verworfenen kurzen Code (ohne Präfix). */
export function kurzcodeHinweis(code: string): string {
  return `Kurzer Code ${code} nicht übernommen – kurze Codes nur mit Scannerpräfix (Einrichtung) oder über die Suche eingeben`;
}

const STANDARD_ZEITGEBER: Zeitgeber = {
  setze: (rueckruf, ms) => setTimeout(rueckruf, ms),
  loesche: (handle) => {
    clearTimeout(handle as ReturnType<typeof setTimeout>);
  },
};

const ABSCHLUSS = new Set(['Enter', 'Tab']);
/** Zeichen eines Codes nach der Zeitregel (Barcodes, Gutscheincodes). */
const CODEZEICHEN = /^[0-9A-Za-z-]$/;
/** Zeichen nach einem Präfix: alle druckbaren ASCII-Zeichen (wie die Codes im Stamm). */
const PRAEFIXZEICHEN = /^[!-~]$/;
/**
 * Umschalttasten: Scanner drücken für Großbuchstaben (Gutscheincode „GS-…“) Shift wie eine
 * Tastatur. Sie unterbrechen eine Folge nicht und werden nicht zurückgehalten.
 */
const UMSCHALT = new Set(['Shift', 'CapsLock']);
/** keydown ohne Zeichen (Android-WebView/IME, keyCode 229) – das Zeichen folgt als beforeinput. */
const OHNE_ZEICHEN = new Set(['Unidentified', 'Process']);

/**
 * Zeichen eines beforeinput-Ereignisses, die als Tasten zählen (nur `insertText` mit Daten,
 * nicht während einer IME-Komposition); sonst null.
 */
export function feldEingabeDaten(e: {
  inputType?: string;
  data?: string | null;
  isComposing?: boolean;
}): string | null {
  if (e.inputType !== 'insertText' || e.isComposing === true) {
    return null;
  }
  return typeof e.data === 'string' && e.data !== '' ? e.data : null;
}
/**
 * Nachlauf eines Scans: Scanner mit Suffix CR+LF bzw. Enter+Tab senden zwei
 * Abschlusszeichen. Weitere Abschlusszeichen so kurz nach einem Scan gehören zum Scan
 * und dürfen keinen Knopf bzw. Dialog auslösen.
 */
export const NACHLAUF_MS = 100;

/** Größter Abstand der Zeichen nach dem Präfix, sonst unvollständig (Standard). */
export const PRAEFIX_ABSTAND_MS = 300;

/**
 * Rest eines abgebrochenen Scans (Codex-Nachprüfung PB-1, Runde 2): Nach einem verworfenen
 * Scan (überlang, Präfix ohne Abschluss in der Frist, fremde Taste mitten im Scan, Folge ohne
 * Abschluss) gehören alle weiteren druckbaren Zeichen bis zum Abschlusszeichen noch zu diesem
 * Scan – **ohne Zeitgrenze**. Sonst begänne der Rest nach einer Pause eine neue Folge
 * (21 Zeichen, Pause, „12345“ + Enter verkaufte Artikel 12345) bzw. ein verspätetes Enter des
 * Scanners schlösse im Feld „Gegeben“ die Barzahlung ab. Der Rest endet mit dem (geschluckten
 * und gemeldeten) Abschlusszeichen, mit einem neuen Scannerpräfix oder mit einer anderen Taste
 * der Kassierkraft (Esc, Rücktaste, Pfeile, Funktionstasten), die dann normal wirkt. Ein neuer
 * Scan ohne Präfix vor dem Abschluss geht dabei verloren – nie still: Das Abschlusszeichen
 * meldet den verworfenen Rest („bitte erneut scannen“).
 */
const RESTZEICHEN = /^[ -~]$/;

/**
 * Abschlusszeichen als Nachlauf eines eben verarbeiteten Scans – auch wenn der Scan den
 * Fokus inzwischen verschoben hat (z. B. Gutscheincode öffnet den Einlösedialog).
 */
export function istNachlauf(key: string, jetzt: number, letzterScan: number): boolean {
  return ABSCHLUSS.has(key) && jetzt - letzterScan <= NACHLAUF_MS;
}

export class ScanErkennung {
  private readonly maxAbstand: number;
  private readonly minLaenge: number;
  private readonly minLaengeMitPraefix: number;
  private readonly maxLaenge: number;
  private readonly praefixAbstand: number;
  private readonly zeitgeber: Zeitgeber;
  private gehalten: { key: string; zeit: number; ziel: unknown }[] = [];
  /** Nach dem Präfix: alle Zeichen bis zum Abschluss gehören zum Code. */
  private praefixAktiv = false;
  private timer: unknown = null;
  private letzterScan = Number.NEGATIVE_INFINITY;
  /** Zeit der letzten Taste eines Präfix-Scans (Präfix bzw. Zeichen danach). */
  private praefixZeit = Number.NEGATIVE_INFINITY;
  /** Rest eines verworfenen Scans wird bis zum Abschluss geschluckt; null = kein Rest. */
  private rest: string | null = null;
  private readonly doppelFilter: DoppelScanFilter | null;

  constructor(private readonly optionen: ScanErkennungOptionen) {
    this.doppelFilter =
      optionen.doppelFilter === undefined ? new DoppelScanFilter() : optionen.doppelFilter;
    this.maxAbstand = optionen.maxAbstandMs ?? 50;
    this.minLaenge = optionen.minLaenge ?? 4;
    this.minLaengeMitPraefix = optionen.minLaengeMitPraefix ?? 8;
    this.maxLaenge = optionen.maxLaenge ?? 20;
    this.praefixAbstand = optionen.praefixAbstandMs ?? PRAEFIX_ABSTAND_MS;
    this.zeitgeber = optionen.zeitgeber ?? STANDARD_ZEITGEBER;
  }

  /**
   * Meldet eine Taste; 'gehalten' heißt: nicht an die Oberfläche weitergeben
   * (sie kommt per onFreigabe nach oder gehört zu einem Scan).
   */
  taste(key: string, zeit: number, ziel?: unknown): 'gehalten' | 'frei' {
    const ergebnis = this.tasteIntern(key, zeit, ziel);
    this.wartendeAusfuehren();
    return ergebnis;
  }

  /**
   * Aktion erst NACH der Entscheidung über gehaltene Tasten ausführen (Vorwahl V1, Codex-Runde 4
   * UI): Ist nichts gehalten, sofort. Sonst wartet sie, bis die Folge freigegeben, als Scan
   * gemeldet oder verworfen ist (nächste Taste bzw. Ablauf der Haltezeit) – so bleibt die
   * Reihenfolge erhalten (eine früher getippte Ziffer vor der Touch-Taste), und ein laufender
   * Scan wird nie durch einen vorzeitigen Abschluss zerlegt. Lockert keine Schutzregel.
   */
  danach(aktion: () => void): void {
    if (this.gehalten.length === 0 && !this.praefixAktiv) {
      aktion();
      return;
    }
    this.wartend.push(aktion);
  }

  private wartend: (() => void)[] = [];

  private wartendeAusfuehren(): void {
    while (this.wartend.length > 0 && this.gehalten.length === 0 && !this.praefixAktiv) {
      this.wartend.shift()?.();
    }
  }

  private tasteIntern(key: string, zeit: number, ziel?: unknown): 'gehalten' | 'frei' {
    if (UMSCHALT.has(key)) {
      return 'frei';
    }
    const praefix = this.optionen.praefix?.() ?? null;
    if (praefix !== null && key === praefix) {
      // ein neues Präfix beginnt einen neuen Scan (auch nach einem verworfenen Rest)
      this.rest = null;
      if (this.praefixAktiv) {
        this.verwerfen('unvollstaendig');
      } else {
        this.freigeben();
      }
      this.praefixAktiv = true;
      this.praefixZeit = zeit;
      this.timerNeu(this.praefixAbstand, () => {
        this.praefixAbgelaufen();
      });
      return 'gehalten';
    }
    if (this.rest !== null) {
      if (ABSCHLUSS.has(key)) {
        // Abschluss des verworfenen Scans: schlucken (auch ein zweites Enter als Nachlauf) und
        // melden – ein verlorener neuer Scan bleibt so nie unbemerkt
        const rest = this.rest;
        this.rest = null;
        this.letzterScan = zeit;
        this.optionen.onVerworfen?.(rest, 'unvollstaendig');
        return 'gehalten';
      }
      if (RESTZEICHEN.test(key)) {
        this.rest += key;
        return 'gehalten';
      }
      // andere Taste der Kassierkraft: der Rest ist vorbei, die Taste wirkt normal
      this.rest = null;
    }
    if (this.praefixAktiv) {
      return this.tasteNachPraefix(key, zeit, ziel);
    }
    const letzte = this.gehalten.at(-1);
    const inFolge = letzte !== undefined && zeit - letzte.zeit <= this.maxAbstand;
    // Codezeichen beginnen bzw. verlängern eine Folge; andere druckbare Zeichen (Code 39/128:
    // „.“, „/“, „+“ …) nur innerhalb einer laufenden schnellen Folge (Prüfung R1, M3)
    if (CODEZEICHEN.test(key) || (inFolge && PRAEFIXZEICHEN.test(key))) {
      if (letzte !== undefined && !inFolge) {
        this.folgeEnde();
        // Aktionen, die auf die eben entschiedene Folge warteten, vor der neuen Folge ausführen
        // (sonst warteten sie bei Abständen zwischen 50 und 70 ms bis zum Ende der Eingabe)
        this.wartendeAusfuehren();
      }
      this.gehalten.push({ key, zeit, ziel });
      if (this.gehalten.length > this.maxLaenge) {
        // Überlange schnelle Folge: Scanner (z. B. Code ohne passenden Abschluss) – verwerfen
        // statt Ziffern in den Ziffernblock zu geben; der Rest bis zum Abschluss bleibt verworfen
        this.verwerfen('unvollstaendig');
        this.rest = '';
        return 'gehalten';
      }
      this.timerNeu(this.maxAbstand + 20, () => {
        this.folgeEnde();
      });
      return 'gehalten';
    }
    if (ABSCHLUSS.has(key) && letzte !== undefined && zeit - letzte.zeit > this.maxAbstand + 20) {
      // Abschluss lange nach dem letzten Zeichen (Zeitstempel des Tastendrucks, nicht der
      // Verarbeitung): menschliche Eingabe, deren Haltezeit nur verspätet abgelaufen ist
      this.folgeEnde();
      return 'frei';
    }
    const minLaenge = praefix === null ? this.minLaenge : this.minLaengeMitPraefix;
    if (ABSCHLUSS.has(key) && this.gehalten.length >= minLaenge) {
      const code = this.gehalten.map((t) => t.key).join('');
      this.leeren();
      this.letzterScan = zeit;
      this.melden(code, zeit);
      return 'gehalten';
    }
    if (ABSCHLUSS.has(key) && this.gehalten.length > 0) {
      // Schnelle Folge, für die Zeitregel zu kurz (Abschluss binnen der Frist): verwerfen
      // statt in den Ziffernblock (PB-1) – Ziffern + Enter wären sonst Menge bzw. Betrag.
      this.letzterScan = zeit;
      this.verwerfen('kurz');
      return 'gehalten';
    }
    if (
      ABSCHLUSS.has(key) &&
      this.gehalten.length === 0 &&
      zeit - this.letzterScan <= NACHLAUF_MS
    ) {
      return 'gehalten';
    }
    if (this.gehalten.length > 0 && this.gehalten.length >= this.minLaenge) {
      // andere Taste mitten in einer scannerschnellen Folge: verwerfen statt freigeben; der
      // Rest der Folge bis zum Abschluss bleibt verworfen
      this.verwerfen('unvollstaendig');
      this.rest = '';
      return 'frei';
    }
    // Taste der Kassierkraft (Esc, Pfeile, Funktionstasten …): ein folgender gleicher Code ist
    // ein neuer, absichtlicher Scan – keine doppelte Meldung
    this.doppelFilter?.zuruecksetzen();
    this.freigeben();
    return 'frei';
  }

  /**
   * Ende einer Folge ohne Abschluss (Pause bzw. Haltezeit abgelaufen): kurze Folgen sind
   * menschliche Eingaben und werden freigegeben; ab `minLaenge` Zeichen in Scannertempo wird
   * verworfen (Scanner ohne bzw. mit verlorenem Abschlusszeichen – nie in den Ziffernblock).
   */
  private folgeEnde(): void {
    const letzte = this.gehalten.at(-1);
    if (letzte !== undefined && this.gehalten.length >= this.minLaenge) {
      this.verwerfen('unvollstaendig');
      this.rest = '';
    } else {
      this.freigeben();
    }
  }

  private tasteNachPraefix(key: string, zeit: number, ziel: unknown): 'gehalten' | 'frei' {
    if (zeit - this.praefixZeit > this.praefixAbstand) {
      // Frist laut Zeitstempel überschritten (Timer verspätet): Scan verwerfen, diese Taste und
      // der Rest bis zum Abschluss gehören noch dazu
      this.verwerfen('unvollstaendig');
      this.rest = '';
      return 'gehalten';
    }
    if (PRAEFIXZEICHEN.test(key)) {
      this.gehalten.push({ key, zeit, ziel });
      this.praefixZeit = zeit;
      if (this.gehalten.length > MAX_PRAEFIX_CODE) {
        this.verwerfen('unvollstaendig');
        this.rest = '';
        return 'gehalten';
      }
      this.timerNeu(this.praefixAbstand, () => {
        this.praefixAbgelaufen();
      });
      return 'gehalten';
    }
    if (ABSCHLUSS.has(key)) {
      const code = this.gehalten.map((t) => t.key).join('');
      this.praefixAktiv = false;
      this.leeren();
      this.letzterScan = zeit;
      if (code !== '') {
        this.melden(code, zeit);
      }
      return 'gehalten';
    }
    // Andere Taste (Leertaste, Esc, Funktionstaste …) mitten im Präfix-Scan: verwerfen und die
    // Taste schlucken – sie gehört zum Scan und darf keinen Knopf auslösen; der Rest ebenso
    this.verwerfen('unvollstaendig');
    this.rest = '';
    return 'gehalten';
  }

  /** Erkannten Scan weitergeben – außer einer doppelten Meldung desselben Scans. */
  private melden(code: string, zeit: number): void {
    if (this.doppelFilter === null || this.doppelFilter.annehmen(code, zeit)) {
      this.optionen.onScan(code);
    }
  }

  /** Präfix-Scan ohne Abschluss in der Frist: verwerfen, Rest bis zum Abschluss schlucken. */
  private praefixAbgelaufen(): void {
    this.verwerfen('unvollstaendig');
    this.rest = '';
  }

  /**
   * Laufende Folge sofort abschließen – mit derselben Entscheidung wie nach Ablauf der Haltezeit
   * (z. B. bevor eine Kachel gewählt wird, damit eine eben getippte Ziffer nicht erst danach
   * ankommt): kurze menschliche Folge → freigeben; scannerschnelle Folge ab `minLaenge` →
   * verwerfen (Rest bis zum Abschluss bleibt verworfen); laufender Präfix-Scan → verwerfen.
   * Anders als {@link freigeben} lockert das keine Schutzregel.
   */
  abschliessen(): void {
    if (this.timer !== null) {
      this.zeitgeber.loesche(this.timer);
      this.timer = null;
    }
    if (this.praefixAktiv) {
      this.praefixAbgelaufen();
    } else {
      this.folgeEnde();
    }
    this.wartendeAusfuehren();
  }

  /** Zurückgehaltene Tasten an die Oberfläche weiterreichen (nie nach einem Präfix). */
  freigeben(): void {
    if (this.praefixAktiv) {
      this.verwerfen('unvollstaendig');
      return;
    }
    const tasten = this.gehalten.map(({ key, ziel }) => ({ key, ziel }));
    this.leeren();
    if (tasten.length > 0) {
      this.optionen.onFreigabe(tasten);
    }
  }

  /** Beim Abbau: Timer stoppen, Gehaltenes noch freigeben (nach einem Präfix: verwerfen). */
  beenden(): void {
    this.freigeben();
  }

  /** Gehaltene Zeichen verwerfen (nie an die Oberfläche) und melden. */
  private verwerfen(grund: 'kurz' | 'unvollstaendig'): void {
    const code = this.gehalten.map((t) => t.key).join('');
    const nachPraefix = this.praefixAktiv;
    this.praefixAktiv = false;
    this.leeren();
    if (code !== '' || (nachPraefix && grund === 'unvollstaendig')) {
      this.optionen.onVerworfen?.(code, grund);
    }
  }

  private timerNeu(ms: number, rueckruf: () => void): void {
    if (this.timer !== null) {
      this.zeitgeber.loesche(this.timer);
    }
    this.timer = this.zeitgeber.setze(() => {
      this.timer = null;
      rueckruf();
      this.wartendeAusfuehren();
    }, ms);
  }

  private leeren(): void {
    this.gehalten = [];
    if (this.timer !== null) {
      this.zeitgeber.loesche(this.timer);
      this.timer = null;
    }
  }
}

/** Erkannter Scan in einem Eingabefeld (siehe {@link FeldScanErkennung}). */
export interface FeldScan {
  code: string;
  /** Feldinhalt vor der Folge. */
  wertVorher: string;
  /** über das Scannerpräfix erkannt */
  praefix: boolean;
  /** schnelle Folge unter `minLaenge` Zeichen (ohne Präfix) – Scan oder sehr schnelles Tippen */
  kurz: boolean;
}

/**
 * Präfix-Scan im Feld abgebrochen (keine Folgetaste in der Frist, Abschluss ohne Code bzw.
 * fremde Taste): Der Aufrufer stellt das Feld auf `wertVorher` zurück (die Zeichen nach dem
 * Präfix stehen schon darin) und verwirft die auslösende Taste.
 */
/**
 * Abschlusszeichen eines verworfenen Präfix-Scan-Rests (Codex-Nachprüfung Runde 2): Rest und
 * Abschluss sind geschluckt; der Aufrufer meldet den Verlust (nie still), das Feld ist unverändert.
 */
export interface FeldRestVerworfen {
  restVerworfen: string;
}

export interface FeldVerworfen {
  verworfen: true;
  code: string;
  wertVorher: string;
}

/**
 * Scan in ein Eingabefeld erkennen, ohne Tasten zurückzuhalten (in Feldern muss die
 * Tastatur sofort schreiben). Gedacht für Zustände, in denen Scans gesperrt sind
 * (z. B. Feld „Gegeben“ im Bar-Dialog): Folgen schnelle Zeichen und am Ende Enter/Tab,
 * meldet `taste` den Code und den Feldinhalt vor der Folge – der Aufrufer verwirft das
 * Abschlusszeichen und stellt das Feld wieder her, damit kein Barcode als Betrag zählt.
 *
 * PB-1: Zeitregel ab `minLaenge` (Standard 4) Zeichen – so zählt auch ein 4–7-stelliger
 * interner Code im Feld „Gegeben“ nicht als Betrag. Kürzere schnelle Folgen mit sofortigem
 * Abschluss meldet `taste` mit `kurz: true`: In gesperrten Feldern verwirft der Aufrufer sie
 * ebenfalls (Feld wird geleert, da Scan und getippte Ziffern nicht sicher zu trennen sind);
 * sonst bleiben sie normale Eingabe. Mit Scannerpräfix: Der Aufrufer verwirft die Präfixtaste
 * (`'praefix'`), danach ist jede Länge bis zum Abschluss ein Scan (`praefix: true`).
 *
 * Ein erkanntes Präfix wird nie still vergessen (Codex-Nachprüfung PB-1): Läuft die Frist ohne
 * Abschluss ab, meldet `ablauf` (Timer des Aufrufers) bzw. die nächste Taste ein
 * {@link FeldVerworfen}; der Rest des Scans bis zum Abschlusszeichen ist danach `'schlucken'`
 * – so löst das verspätete Enter des Scanners im Feld „Gegeben“ keine Barzahlung aus.
 */
export class FeldScanErkennung {
  private zeichen: { key: string; zeit: number }[] = [];
  private wertVorher = '';
  private letzterScan = Number.NEGATIVE_INFINITY;
  /** Zeitpunkt der letzten Taste nach einem Präfix; null = kein Präfix aktiv. */
  private praefixZeit: number | null = null;
  /** Rest eines verworfenen Präfix-Scans (ohne Zeitgrenze bis zum Abschluss); null = keiner. */
  private rest: string | null = null;
  /**
   * Zeichen der letzten keydown-Taste, deren Einfügen (beforeinput) noch aussteht – damit ein
   * Zeichen nicht doppelt zählt, wenn keydown und beforeinput beide ankommen; null = keins.
   */
  private offeneTaste: { key: string; zeit: number } | null = null;

  constructor(
    private readonly maxAbstand = 50,
    private readonly minLaenge = 4,
    private readonly praefixAbstand = PRAEFIX_ABSTAND_MS,
  ) {}

  /**
   * Läuft gerade eine Zeichenfolge (dann ist ein Enter Abschluss, kein Nachlauf)? Alte
   * Zeichen (z. B. vor einem Fokuswechsel getippt) zählen nicht. Ein Präfix-Scan bzw. sein
   * verworfener Rest zählt immer (ohne Nebenwirkung – `taste` entscheidet).
   */
  hatZeichen(jetzt: number): boolean {
    if (this.praefixZeit !== null || this.rest !== null) {
      return true;
    }
    const letzte = this.zeichen.at(-1);
    return letzte !== undefined && jetzt - letzte.zeit <= this.maxAbstand;
  }

  /** Läuft ein Präfix-Scan (der Aufrufer stellt dann einen Timer für {@link ablauf})? */
  praefixLaeuft(): boolean {
    return this.praefixZeit !== null;
  }

  /**
   * Timer des Aufrufers nach der Frist: Präfix-Scan ohne Abschluss verwerfen. null = kein
   * Präfix mehr aktiv bzw. Frist noch nicht abgelaufen.
   */
  ablauf(jetzt: number): FeldVerworfen | null {
    if (this.praefixZeit === null || jetzt - this.praefixZeit <= this.praefixAbstand) {
      return null;
    }
    return this.praefixVerwerfen();
  }

  private praefixVerwerfen(): FeldVerworfen {
    const ergebnis: FeldVerworfen = {
      verworfen: true,
      code: this.zeichen.map((t) => t.key).join(''),
      wertVorher: this.wertVorher,
    };
    this.praefixZeit = null;
    this.zeichen = [];
    this.rest = '';
    return ergebnis;
  }

  /**
   * `wert`: Feldinhalt vor dieser Taste (keydown kommt vor dem Einfügen). Ergebnis
   * `nachlauf`: weiteres Abschlusszeichen direkt nach einem Scan (CR+LF) – ebenfalls verwerfen;
   * `praefix`: die Präfixtaste – nie ins Feld schreiben; `schlucken`: Rest eines verworfenen
   * Präfix-Scans – nie ins Feld, nie als Abschluss; {@link FeldVerworfen}: Präfix-Scan
   * abgebrochen – Feld zurücksetzen, Taste verwerfen; {@link FeldRestVerworfen}: Abschluss
   * des geschluckten Rests – Taste verwerfen und den Verlust melden.
   */
  taste(
    key: string,
    zeit: number,
    wert: string,
    praefix: string | null = null,
  ): FeldScan | FeldVerworfen | FeldRestVerworfen | 'nachlauf' | 'praefix' | 'schlucken' | null {
    if (OHNE_ZEICHEN.has(key)) {
      // Android-WebView (Fokus im Feld, auch inputmode=none): keydown ohne Zeichen; das Zeichen
      // kommt danach als beforeinput (`eingabeZeichen`). Die Folge läuft weiter.
      this.offeneTaste = null;
      return null;
    }
    this.offeneTaste = key.length === 1 ? { key, zeit } : null;
    if (UMSCHALT.has(key)) {
      return null;
    }
    if (praefix !== null && key === praefix) {
      // Läuft schon ein Präfix-Scan, steht dessen Rest im Feld: den Feldinhalt vor dem ersten
      // Präfix behalten, damit der neue Scan auf den sauberen Wert zurücksetzt.
      if (this.praefixZeit === null) {
        this.wertVorher = wert;
      }
      this.zeichen = [];
      this.praefixZeit = zeit;
      this.rest = null;
      return 'praefix';
    }
    if (this.rest !== null) {
      if (ABSCHLUSS.has(key)) {
        const rest = this.rest;
        this.rest = null;
        this.letzterScan = zeit;
        return { restVerworfen: rest };
      }
      if (RESTZEICHEN.test(key)) {
        this.rest += key;
        return 'schlucken';
      }
      // andere Taste der Kassierkraft (Rücktaste, Esc, Pfeile …): Rest vorbei, Taste wirkt normal
      this.rest = null;
    }
    if (this.praefixZeit !== null) {
      if (zeit - this.praefixZeit > this.praefixAbstand) {
        // Frist abgelaufen (Timer des Aufrufers verspätet): verwerfen, Taste gehört dazu
        return this.praefixVerwerfen();
      }
      if (PRAEFIXZEICHEN.test(key)) {
        this.zeichen.push({ key, zeit });
        this.praefixZeit = zeit;
        return null;
      }
      const code = this.zeichen.map((t) => t.key).join('');
      if (ABSCHLUSS.has(key) && code !== '') {
        this.praefixZeit = null;
        this.zeichen = [];
        this.letzterScan = zeit;
        return { code, wertVorher: this.wertVorher, praefix: true, kurz: false };
      }
      // Abschluss ohne Code bzw. fremde Taste mitten im Präfix-Scan: verwerfen
      return this.praefixVerwerfen();
    }
    const vorige = this.zeichen.at(-1);
    const inFolge = vorige !== undefined && zeit - vorige.zeit <= this.maxAbstand;
    if (CODEZEICHEN.test(key) || (inFolge && PRAEFIXZEICHEN.test(key))) {
      const letzte = this.zeichen.at(-1);
      if (letzte === undefined || zeit - letzte.zeit > this.maxAbstand) {
        this.zeichen = [];
        this.wertVorher = wert;
      }
      this.zeichen.push({ key, zeit });
      return null;
    }
    if (ABSCHLUSS.has(key) && this.zeichen.length === 0 && zeit - this.letzterScan <= NACHLAUF_MS) {
      return 'nachlauf';
    }
    const letzte = this.zeichen.at(-1);
    const scan =
      ABSCHLUSS.has(key) &&
      this.zeichen.length > 0 &&
      letzte !== undefined &&
      zeit - letzte.zeit <= this.maxAbstand;
    const ergebnis: FeldScan | null = scan
      ? {
          code: this.zeichen.map((t) => t.key).join(''),
          wertVorher: this.wertVorher,
          praefix: false,
          kurz: this.zeichen.length < this.minLaenge,
        }
      : null;
    if (scan) {
      this.letzterScan = zeit;
    }
    this.zeichen = [];
    return ergebnis;
  }

  /**
   * beforeinput im Feld (`insertText`, Android-WebView): welche Zeichen der Aufrufer noch als
   * Tasten an {@link taste} geben muss. Ein Zeichen, dessen keydown schon gezählt wurde, fällt
   * weg; kam keydown als „Unidentified“ bzw. gar nicht, zählt das Zeichen hier.
   */
  eingabeZeichen(daten: string, zeit: number): string[] {
    const offen = this.offeneTaste;
    this.offeneTaste = null;
    if (offen !== null && offen.key === daten && zeit - offen.zeit <= this.maxAbstand) {
      return [];
    }
    return Array.from(daten);
  }

  /** Nach dem Durchlauf der {@link eingabeZeichen}: diese Zeichen hatten kein keydown. */
  eingabeGezaehlt(): void {
    this.offeneTaste = null;
  }
}
